import React from 'react';
import {gql, useMutation, MutationFunctionOptions} from '@apollo/client';
import {Box, Button, Grid, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';
import {FormikErrors} from 'formik';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {
  CreateIdentityUser as CreateIdentityUserType,
  CreateIdentityUserVariables,
} from '../../schema-types/CreateIdentityUser';
import {COGNITO_USER_FRAGMENT} from '../../graphql/fragments';
import {IdentityGroupEnum} from '../../schema-types/globalTypes';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField, {
  ActionType,
  AutoTextField,
  CheckboxGroupField,
  CheckboxToggleField,
  RadioGroupField,
} from '../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';

export type CreateIdentityUserFormValues = {
  email: string | null;
  phoneNumber: string | null;
  password: string | null;
  confirmPassword: string | null;
  userType?: 'client' | 'staff' | null;
  staffType?: 'adviser' | 'admin' | null;
  groups: IdentityGroupEnum[];
  curoClientContactId: string | null;
  curoStaffId: string | null;
  sendInviteEmail: boolean;
  setPermanentPassword: boolean;
};

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const validatePassword = (password: string): string | null => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('at least 8 characters');
  }
  if (!/\d/.test(password)) {
    errors.push('at least 1 number');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('at least 1 uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('at least 1 lowercase letter');
  }

  return errors.length > 0 ? `Password must contain ${errors.join(', ')}` : null;
};

export const CREATE_IDENTITY_USER_MUTATION = gql`
  ${COGNITO_USER_FRAGMENT}
  mutation CreateIdentityUser(
    $email: String!
    $groups: [IdentityGroupEnum!]!
    $curoClientContactId: String
    $curoStaffId: String
    $password: String
    $phoneNumber: String
    $sendInviteEmail: Boolean
    $setPermanentPassword: Boolean
  ) {
    createIdentityUser(
      email: $email
      groups: $groups
      curoClientContactId: $curoClientContactId
      curoStaffId: $curoStaffId
      password: $password
      phoneNumber: $phoneNumber
      sendInviteEmail: $sendInviteEmail
      setPermanentPassword: $setPermanentPassword
    ) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const useCreateIdentityUser = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onCreateIdentityUser, {data: createUserData, loading: createUserLoading}] =
    useMutation<CreateIdentityUserType, CreateIdentityUserVariables>(
      CREATE_IDENTITY_USER_MUTATION
    );

  const onCreateIdentityUser = async (
    options?: MutationFunctionOptions<CreateIdentityUserType, CreateIdentityUserVariables>
  ) => {
    closeSnackbar();
    const key = enqueueSnackbar('Creating identity user...', {
      variant: 'info',
      persist: true,
    });

    try {
      const result = await _onCreateIdentityUser(options);
      closeSnackbar(key);
      if (result.data?.createIdentityUser?.ok) {
        enqueueSnackbar('Identity user created successfully', {variant: 'success'});
      } else {
        const errorMsg = result.data?.createIdentityUser?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to create identity user: ${errorMsg}`, {
          variant: 'error',
        });
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to create identity user: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onCreateIdentityUser, createUserData, createUserLoading};
};

interface CreateIdentityUserProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const emailField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'email',
  label: 'Email',
  type: ActionType.text,
  email: true,
  size: 'small',
  fullWidth: true,
};

const phoneNumberField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'phoneNumber',
  label: 'Phone Number',
  type: ActionType.text,
  size: 'small',
  optional: true,
  fullWidth: true,
};

const userTypeField: RadioGroupField<CreateIdentityUserFormValues> = {
  id: 'userType',
  label: 'User Type',
  type: ActionType.radioGroup,
  size: 'small',
  options: [
    {label: 'Client', value: 'client'},
    {label: 'Staff', value: 'staff'},
  ],
  horizontal: true,
  horizontalOptionWidthXs: 6,
  fullWidth: true,
};

const staffTypeField: RadioGroupField<CreateIdentityUserFormValues> = {
  id: 'staffType',
  label: 'Staff Type',
  type: ActionType.radioGroup,
  size: 'small',
  options: [
    {label: 'Adviser (can only see their clients)', value: 'adviser'},
    {label: 'Standard administrator (can see all clients)', value: 'admin'},
  ],
  horizontal: true,
  horizontalOptionWidthXs: 6,
  fullWidth: true,
};

const curoClientContactIdField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'curoClientContactId',
  label: 'Curo Client Contact ID',
  type: ActionType.text,
  size: 'small',
  fullWidth: true,
};

const curoStaffIdField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'curoStaffId',
  label: 'Curo Staff ID',
  type: ActionType.text,
  size: 'small',
};

const passwordField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'password',
  label: 'Password',
  type: ActionType.text,
  size: 'small',
  optional: true,
  fullWidth: true,
};

const confirmPasswordField: AutoTextField<CreateIdentityUserFormValues> = {
  id: 'confirmPassword',
  label: 'Confirm Password',
  type: ActionType.text,
  size: 'small',
  optional: true,
  fullWidth: true,
};

const sendInviteEmailField: CheckboxToggleField<CreateIdentityUserFormValues> = {
  id: 'sendInviteEmail',
  label: 'Send Invite Email',
  type: ActionType.checkboxToggle,
  fullWidth: true,
};

const setPermanentPasswordField: CheckboxToggleField<CreateIdentityUserFormValues> = {
  id: 'setPermanentPassword',
  label: 'Set Permanent Password',
  type: ActionType.checkboxToggle,
  fullWidth: true,
};

interface CreateIdentityUserFormProps {
  loading: boolean;
  onCancel: () => void;
}

const CreateIdentityUserForm: React.FC<CreateIdentityUserFormProps> = ({
  loading,
  onCancel,
}) => {
  const {submitForm, values} = useAutoForm<CreateIdentityUserFormValues>();

  const isClient = values?.userType === 'client';
  const isStaff = values?.userType === 'staff';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AutoField field={emailField} />
      </Grid>
      {/* <Grid item xs={12}>
        <AutoField field={phoneNumberField} />
      </Grid> */}
      <Grid item xs={12}>
        <AutoField field={userTypeField} />
      </Grid>

      {isClient && (
        <Grid item xs={12}>
          <AutoField field={curoClientContactIdField} />
        </Grid>
      )}
      {isStaff && (
        <>
          <Grid item xs={12}>
            <AutoField field={staffTypeField} />
          </Grid>
          <Grid item xs={12}>
            <AutoField field={curoStaffIdField} />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <AutoField field={passwordField} />
      </Grid>
      <Grid item xs={12}>
        <AutoField field={confirmPasswordField} />
      </Grid>
      <Grid item xs={12}>
        <AutoField field={sendInviteEmailField} />
      </Grid>
      {/* <Grid item xs={12}>
        <AutoField field={setPermanentPasswordField} />
      </Grid> */}

      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitForm} disabled={loading}>
            Create User
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

const CreateIdentityUserFormWrapped = withAutoFormNew<
  CreateIdentityUserFormValues,
  CreateIdentityUserFormProps
>(CreateIdentityUserForm, undefined, {debugPosition: 'bottom'});

export const CreateIdentityUser: React.FC<CreateIdentityUserProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const {onCreateIdentityUser, createUserLoading} = useCreateIdentityUser();
  const navigate = useNavigate();

  return (
    <ScrollableModal open={open} onClose={onClose} size="medium" autoHeight>
      <>
        <Typography variant="h6" gutterBottom>
          Create Identity User
        </Typography>
        <CreateIdentityUserFormWrapped
          formFields={[]}
          loading={createUserLoading}
          onCancel={onClose}
          initialValueOverrides={{
            email: '',
            // phoneNumber: '',
            password: '',
            confirmPassword: '',
            userType: null,
            staffType: null,
            groups: [],
            curoClientContactId: '',
            curoStaffId: '',
            sendInviteEmail: true,
            setPermanentPassword: false,
          }}
          validate={(values: any) => {
            const errors: FormikErrors<CreateIdentityUserFormValues> = {};

            if (!values.email) {
              errors.email = 'Required';
            } else if (!validateEmail(values.email)) {
              errors.email = 'Enter a valid email';
            }

            if (!values.userType) {
              errors.userType = 'Required';
            }

            const isClient = values.userType === 'client';
            const isStaff = values.userType === 'staff';

            if (isClient && !values.curoClientContactId) {
              errors.curoClientContactId = 'Clients must have a curo client contact id';
            }

            if (isStaff) {
              if (!values.staffType) {
                errors.staffType = 'Staff type is required';
              }
              if (!values.curoStaffId) {
                errors.curoStaffId = 'Staff must have a curo staff id';
              }
            }

            if (values.password) {
              const passwordError = validatePassword(values.password);
              if (passwordError) {
                errors.password = passwordError;
              }
            }

            if (values.password && !values.confirmPassword) {
              errors.confirmPassword = 'Required';
            } else if (values.password && values.confirmPassword !== values.password) {
              errors.confirmPassword = 'Passwords do not match';
            }

            return errors;
          }}
          submit={(values) => {
            if (values.email && values.userType) {
              const groups: IdentityGroupEnum[] = [];
              if (values.userType === 'client') {
                groups.push(IdentityGroupEnum.CLIENT);
              } else if (values.userType === 'staff') {
                if (values.staffType === 'adviser') {
                  groups.push(IdentityGroupEnum.ADVISER);
                } else if (values.staffType === 'admin') {
                  groups.push(IdentityGroupEnum.ONEVIEW);
                }
              }

              const variables: CreateIdentityUserVariables = {
                email: values.email,
                groups,
                curoClientContactId: values.curoClientContactId,
                curoStaffId: values.curoStaffId || undefined,
                password: values.password || undefined,
                phoneNumber: values.phoneNumber || undefined,
                sendInviteEmail: values.sendInviteEmail,
                setPermanentPassword: values.setPermanentPassword,
              };

              onCreateIdentityUser({variables})
                .then((result) => {
                  if (result.data?.createIdentityUser?.ok) {
                    const userId = result.data.createIdentityUser.user?.username;
                    onClose();
                    if (userId) {
                      navigate(`/identity/${userId}`);
                    }
                    if (onSuccess) {
                      onSuccess();
                    }
                  }
                })
                .catch(() => {
                  // do nothing, found in hook
                });
            }
          }}
        />
      </>
    </ScrollableModal>
  );
};
